import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  BaseWebsocketMessage,
  InsWebsocketMessageRequest,
  QueryWebsocketMessageRequest,
  SubWebsocketMessageRequest,
  UpdWebsocketMessageRequest,
  WebsocketEntTypes,
  WebSocketService,
  WSEntityManager,
} from '@qtek/core/websockets-core';
import { ChatStatusPayload, ChatStreamPayload } from '@qtek/shared/models';
import { Observable, filter, map } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AiChatService {
  private ws = inject(WebSocketService);
  private http = inject(HttpClient);

  private readonly entChat: WebsocketEntTypes = 'chat';
  private readonly entEvent: WebsocketEntTypes = 'chatEvent';

  private chatMgr = new WSEntityManager(this.ws, this.entChat);
  private eventMgr = new WSEntityManager(this.ws, this.entEvent);

  readonly chatStatus$: Observable<ChatStatusPayload> = this.ws
    .getWebSocket$()
    .pipe(
      filter((msg: any) => msg.ent === 'chatStatus' && msg.op === 'upd'),
      map((msg: any) => msg.res as ChatStatusPayload)
    );

  /**
   * SD-2155 / SD-2153: incremental token deltas the BE pushes during an Allo
   * response. Wire-only stream (no DDB); the canonical {@link AiChatEvent}
   * still arrives at the end with the same `ts`, at which point the in-flight
   * bubble built from these deltas reconciles against it. Mirrors
   * {@link chatStatus$} for the tool-progress wire path.
   */
  readonly chatStream$: Observable<ChatStreamPayload> = this.ws
    .getWebSocket$()
    .pipe(
      filter((msg: any) => msg.ent === 'chatStream' && msg.op === 'upd'),
      map((msg: any) => msg.res as ChatStreamPayload)
    );

  subscribeChats(
    payload: Omit<SubWebsocketMessageRequest, 'ent' | 'op'>
  ): Observable<BaseWebsocketMessage> {
    const clean = { ...payload };
    delete (clean as any).cmd;
    return this.chatMgr.subscribeEntity(clean, false);
  }

  queryChats(payload: Omit<QueryWebsocketMessageRequest, 'ent' | 'op'>) {
    this.chatMgr.query(payload);
  }

  createChat(payload: Omit<InsWebsocketMessageRequest, 'ent' | 'op'>) {
    const minimal: any = { ...(payload || {}) };
    delete minimal.res;
    this.chatMgr.create(minimal);
  }

  updateChat(payload: Omit<UpdWebsocketMessageRequest, 'ent' | 'op'>) {
    this.chatMgr.update(payload);
  }

  subscribeChatEvents(
    payload: Omit<SubWebsocketMessageRequest, 'ent' | 'op'>
  ): Observable<BaseWebsocketMessage> {
    const clean = { ...payload };
    delete (clean as any).cmd;
    return this.eventMgr.subscribeEntity(clean, false);
  }

  queryChatEvents(payload: Omit<QueryWebsocketMessageRequest, 'ent' | 'op'>) {
    this.eventMgr.query(payload);
  }

  sendEvent(payload: Omit<InsWebsocketMessageRequest, 'ent' | 'op'>) {
    this.eventMgr.create(payload);
  }

  submitFeedback(body: {
    messageId: string;
    rating: 'positive' | 'negative';
    comment?: string;
  }): Observable<unknown> {
    return this.http.post('/api/v1/allo/feedback', body, {
      withCredentials: true,
    });
  }

  /**
   * SD-2326: resolves a proposeAction pending approval. Body shape matches the
   * BE's `AlloApprovalRequest`. Edited values override the proposed `values`
   * map on the card; omit `editedFields` to accept the BE's proposed values
   * unchanged. `decision='reject'` resumes the graph without applying anything.
   */
  submitApproval(body: {
    chatId: string;
    pendingApprovalId: string;
    decision: 'approve' | 'reject';
    editedFields?: Record<string, unknown>;
    reason?: string;
  }): Observable<unknown> {
    return this.http.post('/api/v1/allo/approve-action', body, {
      withCredentials: true,
    });
  }
}
